import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { aiPackage, voiceHints } from "@/mocks/aiPlanData";

const BRAND = "#FF6B35";

type ConfirmState = { hotel: boolean; car: boolean; tour: boolean; guide: boolean };
type Mode = "voice" | "text";

interface Intent {
  type: "hotel" | "car" | "tour" | "guide" | "order" | "plan" | "unknown";
  path?: string;
  label?: string;
}

function detectIntent(text: string): Intent {
  const t = text;
  if (/酒店|住|民宿|客栈|房间|入住/.test(t)) return { type: "hotel", path: "/hotels", label: "查看酒店" };
  if (/租车|车|开车|自驾|用车|司机|接送/.test(t)) return { type: "car", path: "/cars", label: "查看车型" };
  if (/线路|景点|行程|游玩|旅游|攻略|景区|门票/.test(t)) return { type: "route", path: "/routes", label: "查看线路" };
  // TODO: 导游功能暂禁用，后期可能用到
  // if (/导游|讲解|向导|带队/.test(t)) return { type: "guide", path: "/guides", label: "查看导游" };
  if (/订单|我的订单|查订单/.test(t)) return { type: "order", path: "/orders", label: "查看订单" };
  if (/天|晚|人|预算|套餐|规划|安排|度假/.test(t)) return { type: "plan", label: "生成度假套餐" };
  return { type: "unknown" };
}

interface Msg { id: number; role: "user" | "ai"; text: string; intent?: Intent; isTyping?: boolean; }

// ── Voice Wave ─────────────────────────────────────────────
function VoiceWave({ active }: { active: boolean }) {
  const h = [0.3, 0.5, 0.75, 1, 0.65, 0.88, 0.6, 1, 0.72, 0.45, 0.3];
  return (
    <div className="flex items-center justify-center gap-[3px]" style={{ height: 28 }}>
      {h.map((v, i) => (
        <div key={i} className="rounded-full" style={{
          width: 3,
          height: active ? v * 28 : 3,
          background: active ? "#fff" : "rgba(255,255,255,0.6)",
          transition: "height 0.12s ease",
          animation: active ? `wvBar 0.85s ease-in-out ${i * 0.07}s infinite alternate` : "none",
        }} />
      ))}
    </div>
  );
}

// ── Product Card ───────────────────────────────────────────
function ProductCard({ icon, title, confirmed, onToggle, children, price, image }: {
  icon: string; title: string; confirmed: boolean; onToggle: () => void;
  children: React.ReactNode; price: number; image: string;
}) {
  return (
    <div
      className="flex gap-3 p-3 rounded-2xl transition-all duration-200"
      style={{
        background: confirmed ? "#FFF6F2" : "#fff",
        border: `1.5px solid ${confirmed ? BRAND : "#EBEBEB"}`,
      }}
    >
      <img src={image} alt={title} className="w-[60px] h-[60px] rounded-xl object-cover object-top flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1 mb-0.5">
          <div className="flex items-center gap-1 min-w-0">
            <span style={{ fontSize: 13 }}>{icon}</span>
            <span className="text-sm font-bold text-gray-900 truncate">{title}</span>
          </div>
          <span className="font-bold text-base flex-shrink-0" style={{ color: BRAND }}>¥{price}</span>
        </div>
        <div className="text-xs text-gray-400 leading-relaxed mb-2">{children}</div>
        <div className="flex justify-end">
          <button
            onClick={onToggle}
            className="px-3.5 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 whitespace-nowrap"
            style={{ background: confirmed ? BRAND : "#F0F0F0", color: confirmed ? "#fff" : "#999" }}
          >
            {confirmed ? <span className="flex items-center gap-1"><i className="ri-check-line" style={{ fontSize: 11 }} />已选</span> : "加入"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Chat Bubble ────────────────────────────────────────────
function ChatBubble({ msg, onNav }: { msg: Msg; onNav: (p: string) => void }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
      {!isUser && (
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)" }}
        >
          <i className="ri-sparkling-2-fill text-white" style={{ fontSize: 14 }} />
        </div>
      )}
      <div className={`max-w-[78%] flex flex-col gap-2 ${isUser ? "items-end" : "items-start"}`}>
        <div
          className="px-4 py-3 text-sm leading-relaxed"
          style={{
            background: isUser ? `linear-gradient(135deg, ${BRAND}, #FF8C42)` : "#fff",
            color: isUser ? "#fff" : "#1F2937",
            borderRadius: isUser ? "18px 4px 18px 18px" : "4px 18px 18px 18px",
            whiteSpace: "pre-line",
            border: isUser ? "none" : "1px solid #F0F0F0",
          }}
        >
          {msg.isTyping ? (
            <div className="flex gap-1 items-center py-0.5">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300"
                  style={{ animation: `bDot 1s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          ) : msg.text}
        </div>
        {!isUser && msg.intent?.path && !msg.isTyping && (
          <button
            onClick={() => onNav(msg.intent!.path!)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold cursor-pointer active:scale-95 transition-all whitespace-nowrap text-white"
            style={{ background: BRAND }}
          >
            <i className="ri-arrow-right-up-line" style={{ fontSize: 12 }} />{msg.intent.label}
          </button>
        )}
        {!isUser && msg.intent?.type === "plan" && !msg.isTyping && (
          <button
            onClick={() => onNav("__generate__")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold cursor-pointer active:scale-95 transition-all whitespace-nowrap text-white"
            style={{ background: BRAND }}
          >
            <i className="ri-sparkling-2-line" style={{ fontSize: 12 }} />生成度假套餐
          </button>
        )}
      </div>
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="flex gap-3 p-3 rounded-2xl animate-pulse" style={{ background: "#fff", border: "1px solid #F0F0F0" }}>
      <div className="w-[60px] h-[60px] rounded-xl bg-gray-100 flex-shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-3 bg-gray-100 rounded-full w-3/4" />
        <div className="h-2.5 bg-gray-100 rounded-full w-1/2" />
        <div className="h-2.5 bg-gray-100 rounded-full w-2/3" />
      </div>
    </div>
  );
}

export default function AiPlanPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPostState = (location.state as {
    fromPost?: boolean;
    postTitle?: string;
    postTags?: string[];
    relatedProducts?: { name: string; type: string; price: string }[];
  } | null);

  const pkg = aiPackage;
  const [mode, setMode] = useState<Mode>("text");
  const [inputText, setInputText] = useState("");
  const [isRec, setIsRec] = useState(false);
  const [recSec, setRecSec] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [confirmed, setConfirmed] = useState<ConfirmState>({ hotel: false, car: false, tour: false, guide: false });
  const [checking, setChecking] = useState(false);
  const [hintIdx, setHintIdx] = useState(0);

  const buildInitMsgs = (): Msg[] => {
    if (fromPostState?.fromPost && fromPostState.postTitle) {
      const productNames = fromPostState.relatedProducts?.map(p => `· ${p.name}`).join("\n") ?? "";
      return [
        {
          id: 0, role: "ai",
          text: "你好！我是美伊 AI ✨\n说出旅行需求，我来帮你规划\n\n试试：「三亚5天4晚，4个人，预算2万」",
        },
        {
          id: 1, role: "user",
          text: `我看了「${fromPostState.postTitle}」这篇游记，根据里面的关联产品帮我规划一个旅行计划`,
        },
        {
          id: 2, role: "ai",
          text: `收到！已分析「${fromPostState.postTitle}」里的关联产品 🎯\n\n包含以下产品：\n${productNames}\n\n正在为你生成专属旅行套餐，稍候...`,
          intent: { type: "plan", label: "生成度假套餐" },
        },
      ];
    }
    return [{
      id: 0, role: "ai",
      text: "你好！我是美伊 AI ✨\n说出旅行需求，我来帮你规划\n\n试试：「三亚5天4晚，4个人，预算2万」",
    }];
  };

  const [msgs, setMsgs] = useState<Msg[]>(buildInitMsgs);
  const [mid, setMid] = useState(fromPostState?.fromPost ? 3 : 1);
  const endRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sendRef = useRef<(text: string) => void>(() => {});

  useEffect(() => {
    const t = setInterval(() => setHintIdx(i => (i + 1) % voiceHints.length), 3200);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // 来自帖子时，自动触发套餐生成
  useEffect(() => {
    if (fromPostState?.fromPost) {
      const timer = setTimeout(() => {
        setLoading(true);
        setGenerated(false);
        setTimeout(() => { setLoading(false); setGenerated(true); }, 1800);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [fromPostState?.fromPost]);

  const startRec = () => {
    setIsRec(true); setRecSec(0);
    timer.current = setInterval(() => setRecSec(s => s + 1), 1000);
  };

  const send = (text: string) => {
    if (!text.trim()) return;
    const uMsg: Msg = { id: mid, role: "user", text };
    const tMsg: Msg = { id: mid + 1, role: "ai", text: "", isTyping: true };
    setMsgs(p => [...p, uMsg, tMsg]);
    setMid(p => p + 2);
    setInputText("");
    const intent = detectIntent(text);
    setTimeout(() => {
      const reply = buildReply(text, intent);
      setMsgs(p => p.map(m => m.id === mid + 1 ? { ...m, isTyping: false, text: reply, intent } : m));
      if (intent.type === "plan") {
        setTimeout(() => { setLoading(true); setGenerated(false); setTimeout(() => { setLoading(false); setGenerated(true); }, 1800); }, 500);
      }
    }, 900);
  };
  sendRef.current = send;

  const stopRec = useCallback(() => {
    if (!isRec) return;
    setIsRec(false);
    if (timer.current) clearInterval(timer.current);
    const phrases = ["三亚5天4晚度假套餐，4个人，预算两万", "帮我找个海景酒店", "我需要一辆商务车租三天", "推荐亲子线路"];
    sendRef.current(phrases[Math.floor(Math.random() * phrases.length)]);
  }, [isRec]);

  const buildReply = (text: string, intent: Intent): string => {
    switch (intent.type) {
      case "hotel": return "好的！已为你筛选到优质酒店 🏨\n按评分和价格排序，点下方查看详情。";
      case "car": return "明白！为你找到合适的租车方案 🚗\n可查看车型、取还车信息和价格。";
      case "tour": return "没问题！为你推荐热门线路 🗺️\n含导游、景区门票和行程安排。";
      case "guide": return "好的！为你筛选专业导游 👋\n可按语言、评分和服务类型筛选。";
      case "order": return "马上帮你查看订单状态 📋\n点下方进入订单中心。";
      case "plan": return `收到！正在为你规划度假套餐 ✨\n美伊AI正在智能匹配最优方案，稍候...`;
      default: return "告诉我更多旅行细节吧 😊\n\n· 想去哪里？\n· 几天几晚，几个人？\n· 需要酒店、租车、线路或导游？";
    }
  };

  const handleNav = (path: string) => {
    if (path === "__generate__") {
      setLoading(true); setGenerated(false);
      setTimeout(() => { setLoading(false); setGenerated(true); }, 1800);
      return;
    }
    navigate(path);
  };

  const toggle = (k: keyof ConfirmState) => setConfirmed(p => ({ ...p, [k]: !p[k] }));
  const cnt = Object.values(confirmed).filter(Boolean).length;
  const total = (confirmed.hotel ? pkg.hotel.price : 0) + (confirmed.car ? pkg.car.price : 0) + (confirmed.tour ? pkg.tour.price : 0) + (confirmed.guide ? pkg.guide.price : 0);

  const book = () => {
    setChecking(true);
    setTimeout(() => { setChecking(false); navigate("/inquiry-orders"); }, 1500);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#F5F5F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}
    >
      {/* ── Premium Header ── */}
      <div
        className="flex-shrink-0 relative overflow-hidden"
        style={{ background: "linear-gradient(145deg,#1A1A2E 0%,#16213E 60%,#0F3460 100%)" }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute rounded-full" style={{ width: 200, height: 200, top: -60, right: -40, background: "rgba(255,107,53,0.1)", filter: "blur(40px)" }} />
          <div className="absolute rounded-full" style={{ width: 100, height: 100, bottom: -20, left: 30, background: "rgba(255,107,53,0.07)", filter: "blur(25px)" }} />
        </div>
        <div className="relative px-5 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* AI avatar glow */}
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)", filter: "blur(8px)", opacity: 0.5, transform: "scale(1.2)" }}
                />
                <div
                  className="relative w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #FF6B35, #FF9A5C)" }}
                >
                  <i className="ri-sparkling-2-fill text-white" style={{ fontSize: 20 }} />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-white">美伊 AI</span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(255,107,53,0.2)", color: "#FF9A5C", border: "1px solid rgba(255,107,53,0.3)" }}
                  >Beta</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981" }} />
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>智能旅行规划助手</p>
                </div>
              </div>
            </div>
            <button
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs cursor-pointer whitespace-nowrap"
              style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <i className="ri-history-line" style={{ fontSize: 13 }} />历史
            </button>
          </div>
        </div>
      </div>

      {/* ── Chat area ── */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        style={{ paddingBottom: generated ? 260 : 200 }}
      >
        {msgs.map(m => <ChatBubble key={m.id} msg={m} onNav={handleNav} />)}

        {/* Quick chips */}
        {msgs[msgs.length - 1]?.role === "ai" && !msgs[msgs.length - 1]?.isTyping && !generated && !loading && (
          <div className="flex flex-wrap gap-2 ml-10">
            {["三亚5天4人套餐", "找海景酒店", "租商务车", "需要导游"].map(chip => (
              <button
                key={chip}
                onClick={() => send(chip)}
                className="px-3.5 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap active:scale-95 transition-all"
                style={{ background: "#FFF3EE", color: BRAND, border: `1px solid #FFD4BD` }}
              >{chip}</button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="space-y-2.5">
            <p className="text-xs text-gray-400 ml-10">美伊正在为你规划套餐...</p>
            <Skeleton /><Skeleton /><Skeleton />
          </div>
        )}

        {/* Generated package */}
        {generated && (
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <i className="ri-sparkling-2-fill" style={{ color: BRAND, fontSize: 14 }} />
                <span className="text-sm font-bold text-gray-900">美伊推荐套餐</span>
              </div>
              <button
                onClick={() => { setLoading(true); setGenerated(false); setTimeout(() => { setLoading(false); setGenerated(true); }, 1400); }}
                className="text-xs font-medium cursor-pointer active:scale-95 whitespace-nowrap flex items-center gap-1"
                style={{ color: BRAND }}
              >
                <i className="ri-refresh-line" style={{ fontSize: 12 }} />换一换
              </button>
            </div>
            <ProductCard icon="🏨" title={pkg.hotel.name} confirmed={confirmed.hotel} onToggle={() => toggle("hotel")} price={pkg.hotel.price} image={pkg.hotel.image}>
              <div>⭐ {pkg.hotel.rating} · {pkg.hotel.address}</div>
              <div>入住 {pkg.hotel.checkIn} ~ 离店 {pkg.hotel.checkOut}</div>
            </ProductCard>
            <ProductCard icon="🚗" title={pkg.car.model} confirmed={confirmed.car} onToggle={() => toggle("car")} price={pkg.car.price} image={pkg.car.image}>
              <div>取车：{pkg.car.pickupLocation}</div>
              <div>{pkg.car.pickupTime} ~ {pkg.car.returnTime}</div>
            </ProductCard>
            <ProductCard icon="🗺️" title={pkg.tour.name} confirmed={confirmed.tour} onToggle={() => toggle("tour")} price={pkg.tour.price} image={pkg.tour.image}>
              <div>{pkg.tour.summary}</div><div>{pkg.tour.dates}</div>
            </ProductCard>
            <ProductCard icon="👩‍✈️" title={`导游 · ${pkg.guide.name}`} confirmed={confirmed.guide} onToggle={() => toggle("guide")} price={pkg.guide.price} image={pkg.guide.avatar}>
              <div>语言：{pkg.guide.language} · ⭐ {pkg.guide.rating}</div>
              <div>服务日期：{pkg.guide.dates}</div>
            </ProductCard>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* ── Generated bottom bar ── */}
      {generated && (
        <div className="fixed left-0 right-0 z-40 px-4 py-4" style={{ bottom: 0, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs text-gray-400 mb-0.5">套餐总价</div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold" style={{ color: BRAND }}>¥{total.toLocaleString()}</div>
                <div className="text-xs text-gray-400">顾问报价后确认付款</div>
              </div>
            </div>
            <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full">{cnt}/4 已选</span>
          </div>
          {/* 需求询单提示 */}
          <div
            className="flex items-start gap-2 px-3 py-2 rounded-xl mb-3"
            style={{ background: "#FFF8F0", border: "1px solid #FFE4CC" }}
          >
            <i className="ri-customer-service-2-line flex-shrink-0 mt-0.5" style={{ color: BRAND, fontSize: 13 }} />
            <p className="text-xs leading-relaxed" style={{ color: "#92400E" }}>
              选定产品后提交<strong>需求询单</strong>，专属顾问将核对产品并给您报价，确认后完成支付即正式成单
            </p>
          </div>
          <button
            onClick={cnt > 0 ? book : undefined}
            disabled={cnt === 0}
            className="w-full h-12 rounded-2xl text-sm font-bold whitespace-nowrap active:scale-[0.98] transition-all"
            style={{
              background: cnt > 0 ? `linear-gradient(135deg, ${BRAND}, #FF9A5C)` : "#F0F0F0",
              color: cnt > 0 ? "#fff" : "#C0C4CC",
              cursor: cnt > 0 ? "pointer" : "not-allowed",
            }}
          >
            {checking
              ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />提交中...</span>
              : cnt > 0
                ? <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line" style={{ fontSize: 15 }} />
                    提交询单（已选 {cnt}/4 项产品）
                  </span>
                : "请先选择产品"
            }
          </button>
        </div>
      )}

      {/* ── Input bar ── */}
      <div
        className="flex-shrink-0 bg-white z-40"
        style={{
          borderTop: "1px solid #F0F0F0",
          paddingBottom: "calc(4px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {/* Mode toggle */}
        <div className="flex justify-center pt-3 pb-2 px-5">
          <div
            className="flex p-1 rounded-full w-full max-w-xs"
            style={{ background: "#F0F0F0" }}
          >
            {(["voice", "text"] as Mode[]).map(m => {
              const act = mode === m;
              return (
                <button
                  key={m}
                  onClick={() => { setMode(m); if (m === "text") setTimeout(() => inputRef.current?.focus(), 80); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
                  style={{
                    background: act ? "#fff" : "transparent",
                    color: act ? "#1F2937" : "#9CA3AF",
                    boxShadow: act ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                  }}
                >
                  <i className={m === "voice" ? "ri-mic-line" : "ri-keyboard-line"} style={{ fontSize: 13 }} />
                  {m === "voice" ? "语音" : "文字"}
                </button>
              );
            })}
          </div>
        </div>

        {/* Voice mode */}
        {mode === "voice" && (
          <div className="flex flex-col items-center gap-3 px-5 pb-3">
            {/* Hint text */}
            <p className="text-xs text-center" style={{ color: "#B0B5BE", minHeight: 16 }}>
              {isRec
                ? <span style={{ color: BRAND, fontWeight: 600 }}>录音中 {recSec}s · 松手发送</span>
                : voiceHints[hintIdx]}
            </p>
            {/* Mic button with rings */}
            <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
              {isRec && (
                <>
                  <div className="absolute rounded-full" style={{ width: 96, height: 96, border: `2px solid rgba(255,107,53,0.25)`, animation: "mRing 1.4s ease-out infinite" }} />
                  <div className="absolute rounded-full" style={{ width: 80, height: 80, border: `2px solid rgba(255,107,53,0.35)`, animation: "mRing 1.4s ease-out 0.2s infinite" }} />
                </>
              )}
              <button
                onMouseDown={startRec}
                onMouseUp={stopRec}
                onTouchStart={e => { e.preventDefault(); startRec(); }}
                onTouchEnd={e => { e.preventDefault(); stopRec(); }}
                className="relative flex items-center justify-center rounded-full cursor-pointer select-none transition-all"
                style={{
                  width: 68, height: 68,
                  background: isRec
                    ? `linear-gradient(145deg, #FF5722, #FF8A50)`
                    : `linear-gradient(145deg, #FF6B35, #FF9A5C)`,
                  boxShadow: isRec ? `0 0 28px rgba(255,87,34,0.5)` : "0 6px 20px rgba(255,107,53,0.4)",
                  transform: isRec ? "scale(1.05)" : "scale(1)",
                }}
              >
                {isRec ? <VoiceWave active /> : <i className="ri-mic-2-line text-white" style={{ fontSize: 28 }} />}
              </button>
            </div>
            <p className="text-xs" style={{ color: "#CECECE" }}>{isRec ? "" : "按住麦克风说话"}</p>
          </div>
        )}

        {/* Text mode */}
        {mode === "text" && (
          <div className="flex items-center gap-2 px-4 pb-3">
            <div
              className="flex-1 flex items-center gap-2 rounded-2xl px-4 py-2.5"
              style={{ background: "#F5F5F7", border: "1px solid #EBEBEB" }}
            >
              <i className="ri-sparkling-2-line" style={{ color: BRAND, fontSize: 14 }} />
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send(inputText)}
                placeholder="说说你的旅行想法..."
                className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                style={{ fontSize: 14 }}
              />
            </div>
            <button
              onClick={() => send(inputText)}
              className="w-11 h-11 flex items-center justify-center rounded-2xl flex-shrink-0 cursor-pointer active:scale-95 transition-all"
              style={{
                background: inputText.trim() ? `linear-gradient(135deg, ${BRAND}, #FF9A5C)` : "#F0F0F0",
              }}
            >
              <i className="ri-arrow-up-line" style={{ color: inputText.trim() ? "#fff" : "#C0C4CC", fontSize: 18 }} />
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes bDot { 0%,80%,100%{transform:translateY(0);opacity:.35} 40%{transform:translateY(-5px);opacity:1} }
        @keyframes wvBar { from{transform:scaleY(0.3)} to{transform:scaleY(1)} }
        @keyframes mRing { 0%{transform:scale(0.85);opacity:.4} 100%{transform:scale(1.3);opacity:0} }
      `}</style>
    </div>
  );
}
